import { Component, ElementRef, HostListener, Input, input } from '@angular/core';
import { BehaviorSubject, debounceTime, forkJoin, map, Subject, Subscription, switchMap } from 'rxjs';
import { QuickNavApiService } from '../../services/quick-nav-api.service';
import { CategoryAPIName, eSearchType, IAzureIndexResponseFacetModel, IQuickNavResultTypeToUrl, IQuickSearchItem, 
  IQuickSearchResponseItem, QuickSearchCategory, QuickSearchCategoryNameMap, IQuickNavInfoResponse } from '../../../core/models/quick-nav.model';
import { CommonModule, NgFor, NgIf } from '@angular/common';
import { MatIcon } from '@angular/material/icon';
import { InputComponent } from '../input/input.component';
import { Router } from '@angular/router';
import { P } from '@angular/cdk/portal-directives.d-BoG39gYN';
import { environment } from '../../../../environments/environment';

type QuickSearchCustomItem = { [key: string]: any };

@Component({
  selector: 'eap-quick-search',
  imports: [CommonModule], // InputComponent, MatIcon
  standalone: true,
  providers: [
    QuickNavApiService
  ],
  templateUrl: './quick-search.component.html',
  styleUrl: './quick-search.component.scss'
})
export class QuickSearchComponent {

  @Input() searchTextInput: Subject<string> = new Subject<string>();
  // @Output() loadingTrigger = new EventEmitter<boolean>();
  // @Output() hideAndResetQuickSearch = new EventEmitter<any>();

  public searchText: string = '';
  public isLoading = false;
  // public quickSearchCategory: { Key: QuickSearchCategory, Name: string, Count: number }[] = [
  //   {
  //     Key: QuickSearchCategory.person,
  //     Name: QuickSearchCategoryNameMap.get(QuickSearchCategory.person)!,
  //     Count: 0
  //   },
  //   {
  //     Key: QuickSearchCategory.cases,
  //     Name: QuickSearchCategoryNameMap.get(QuickSearchCategory.cases)!,
  //     Count: 0
  //   }
  // ];
  // public selectedCategory: QuickSearchCategory = QuickSearchCategory.all;
  public quickSearchCategoryItems: IQuickSearchItem[] = [];
  public quickSearchRecentlyViewedItems: IQuickSearchItem[] = [];
  public shownRecentlyViewedItems: IQuickSearchItem[] = [];
  //public shownRecentlyViewedItems: { [key: string]: string; }[] = [];
  public searchTermChanged$ = new Subject<string>();
  public isFocused: boolean = false;
  public isSearchResultOpen: boolean = false;
  public QuickSearchCategory = QuickSearchCategory;

  private subscriptions = new Subscription();
  public totalAmount: number = 0;


  constructor(
    private quickNavService: QuickNavApiService,
    private router: Router,
    private el: ElementRef
  ) {

  }

  ngOnInit(): void {
    this.subscriptions.add(
      this.searchTermChanged$.pipe(debounceTime(900)).subscribe((text) => {
        if (!text || (text.length < 2 && text && this.searchText === text)) {
          this.searchText = text;
          this.quickSearchCategoryItems = [];
          // this.quickSearchCategory.forEach(item => {
          //   item.Count = 0;
          // });
          this.isLoading = false;
          this.isSearchResultOpen = false;
          this.showRecentlyViewed();
          return;
        }
        this.searchText = text;
        this.updateShownRecentlyViewedItems();
        this.processData();
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  get viewedResultsMessage(): string {
    const itemsLengthText =
      this.shownRecentlyViewedItems?.length > 0
        ? `${this.shownRecentlyViewedItems?.length}`
        : 'No results found';

    return `Recently viewed (${itemsLengthText})`;
  }

  private recentlyViewedItemContainsWords(searchWords: string[], recentlyViewedItem: IQuickSearchItem): boolean {
    if (searchWords.length === 0) {
      return false;
    }
    return searchWords.every((word) =>
      Object.entries(recentlyViewedItem)
        .filter(
          ([key, value]) =>
            (key !== 'URL' && typeof value === 'string') || Array.isArray(value)
        )
        .some(([key, value]) => {
          const values = Array.isArray(value) ? value : [value];
          return values.some((v) =>
            (v as string).toLowerCase().includes(word.toLowerCase())
          );
        })
    );
  }

  updateShownRecentlyViewedItems() {
    const filteredItems = this.filterRecentlyViewedItems();

    this.shownRecentlyViewedItems = this.searchText
      ? this.highlightSearchItemText(filteredItems)
      : filteredItems;
  }

  
  private highlightSearchItemText(filteredItems: IQuickSearchItem[]) : IQuickSearchItem[] {
    const searchWords = this.getSearchWords();

    const arrQuickSearchItemsCombined: IQuickSearchItem[] = [];    
    if( this.quickSearchCategoryItems ){
      this.quickSearchCategoryItems.forEach(ci => arrQuickSearchItemsCombined.push(ci));  
    }          
    return filteredItems.map((item) => {
      const highlightedItem: QuickSearchCustomItem = item;      
      const quickNavItem: any = arrQuickSearchItemsCombined.find(qi => qi.Name == item['Name']);     
      if( quickNavItem ) {
        //console.log(`Found matching quick nav item for name: '${item.Name}'`);
        //const customItem: QuickSearchCustomItem = {...quickNavItem};

        //return customItem;
        return quickNavItem;
      }

      for (const [key, value] of Object.entries(item)) {        
        if (typeof value === 'string') {
          const lowerValue = value.toLowerCase();
          const highlightedWords = new Set<string>();
          let highlightedValue = value;

          for (const searchWord of searchWords) {
            if (lowerValue.includes(searchWord)) {
              const regex = new RegExp(
                `(?<!<[^>]{0,1000})${searchWord}(?![^<]*>)`,
                'gi'
              );

              if (!highlightedWords.has(searchWord)) {                                                

                highlightedValue = highlightedValue.replace(
                  regex,
                  '<span class="quick-search-highlighted-text">$&</span>'
                );
                highlightedWords.add(searchWord);
              }
            }
          }

          highlightedItem[key] = highlightedValue;
        } else {
          highlightedItem[key] = value;
        }
      }

      const returnItem: IQuickSearchItem = {
        ...highlightedItem,
        Name: highlightedItem['Name'],
        ResultIcon: highlightedItem['ResultIcon'],
        CategoryType: highlightedItem['CategoryType']
      };
      return returnItem;
    });
  }

   //private filterRecentlyViewedItems(): QuickSearchCustomItem[] {
   private filterRecentlyViewedItems(): IQuickSearchItem[] {
    //this.updateRecentlyViewedValuesFromQuickNavDataset();
    const searchWords = this.getSearchWords();
    //return this.quickSearchRecentlyViewedItems.reduce((acc: QuickSearchCustomItem[], recentlyViewedItem: IQuickSearchItem) => {      
    return this.quickSearchRecentlyViewedItems.reduce((acc: IQuickSearchItem[], recentlyViewedItem: IQuickSearchItem) => {      
      const isMatchingSearchText =
        !this.searchText ||
        this.recentlyViewedItemContainsWords(searchWords, recentlyViewedItem);

      if (isMatchingSearchText) {
        const updatedItem: QuickSearchCustomItem = { ...recentlyViewedItem };

        Object.entries(updatedItem).forEach(([key, value]) => {
          if (Array.isArray(value)) {
            updatedItem[key] = value.filter(valueStr => {
              if (valueStr && searchWords.length) {
                return searchWords.some(word =>
                  valueStr.toLowerCase().includes(word)
                );
              } else {
                return false;
              }
            })[0]?.toString();
          }
        });
        const pushItem: IQuickSearchItem = {
          ...updatedItem,
          Name: updatedItem['Name'],
          ResultIcon: updatedItem['ResultIcon'],
          CategoryType: updatedItem['CategoryType']          
        };
        acc.push(pushItem);
      }
      return acc;
    }, []);
  }

  private getSearchWords(): string[] {
    return this.searchText.toLowerCase().split(/[\W_]+/).filter(word => word.length >= 2);
  }

 /**
  * This method redirect to party search page.
  */
  public onAdvanceSearchButtonClick() {
    this.router.navigateByUrl('search');
  }

  public searchTermChanged(event: any): void {
    let searchTerm = event.target?.value
    this.searchTermChanged$.next(searchTerm);
  }

  @HostListener('document:click', ['$event'])
  public onBodyClick(event: MouseEvent) {
    if (!this.el.nativeElement.contains(event.target)) {
      this.isSearchResultOpen = false;
    }
  }

  public onSelectCategory(category: QuickSearchCategory) {
    // this.selectedCategory = category;
    this.processData();
  }

  public isFieldHighlighted(field: string | string[] | undefined): boolean {
    return !!(field && field.includes('highlighted-text'));
  }

  public getFormattedFileNo(item: IQuickSearchItem): string {
    const prefixSymbol = "-";
    const isFieldHighlighted = this.isFieldHighlighted(item?.FileNo);
    let isAlreadyTransformed = false;

    if (item?.FileNo == null || item?.FileNo.length < 3)
      return item?.FileNo || '';

    let insertIndex = 3;
    if (item?.FileNo[insertIndex] === prefixSymbol) {
      isAlreadyTransformed = true;
    }

    if (isFieldHighlighted) {
      const digitToCheck = item?.FileNo.replace(/(<([^>]+)>)/ig, '');
      if (digitToCheck.length > 3) {
        insertIndex = item?.FileNo.indexOf(digitToCheck[2]) + 1;
        if (digitToCheck[3] === prefixSymbol) {
          isAlreadyTransformed = true;
        }
      }
    }

    return isAlreadyTransformed
      ? item?.FileNo
      : item?.FileNo.slice(0, insertIndex) +
      prefixSymbol +
      item?.FileNo.slice(insertIndex);
  }

  public getTitleName(field: string | string[] | undefined): string {
    return field?.toString().replace(/<\/?[^>]+(>|$)/g, '') ?? '';
  }

  public formatTerminationDate(dot: string): string {
    if (dot) {
      const [year, day, month] = dot.split('-');
      const strDate = `${year}-${month}-${day}`;
      return (strDate ? strDate : '');
    }
    return '';
  }

  public onFileNoClick(item: IQuickSearchItem, isCivilCase: boolean) {
    window.open(`${environment.rootUrl}${isCivilCase? 'civil-case' : 'case-info'}/${item.FileNo?.replace(/(<([^>]+)>)/ig, '')}`, '_blank');
  }
  
  public onPersonClick(item: IQuickSearchItem) {
    window.open(`${environment.rootUrl}party-search-result?defendantNumber=${item.PersonNumber}`, '_blank');
  }

  public onQuickNavItemClick(item: IQuickSearchItem) {    
    const strNavUrl = `${(environment.rootUrl?.endsWith('/') ? environment.rootUrl : environment.rootUrl + '/')}${item.URL}`;
    console.log(`Going to open url: ${strNavUrl} in new window`);
    window.open(strNavUrl, '_blank');
  }

  public showRecentlyViewed() {    
    if ((!this.searchText && !this.isSearchResultOpen)) {
      this.isSearchResultOpen = true;
      this.isLoading = true;
      this.quickNavService.freshQuickNavInfoResponse.pipe(
        switchMap((response) => {
          return this.quickNavService.getRecentLocations().pipe(
            map((results) => {
              this.isLoading = false;
              this.quickSearchRecentlyViewedItems = 
                results.SearchResults?.map((item: IQuickSearchResponseItem) => this.mapQuickSearchItemData(item, response?.MappingList ?? [], true)) ?? [];
              this.updateShownRecentlyViewedItems();
            })
          )
        })
      ).subscribe();   
    }   
  }
  
  public getIconByCategoryType(category: QuickSearchCategory) {
    switch (category) {
      case QuickSearchCategory.person:
      case QuickSearchCategory.officer:
        return "assets/category-type/person.svg";
      case QuickSearchCategory.crimcase:
      case QuickSearchCategory.civilcase:
        return "assets/category-type/case.svg";
      case QuickSearchCategory.crimdoc:
      case QuickSearchCategory.civildoc:
      case QuickSearchCategory.persondoc:
        return "assets/category-type/document.svg";
      default:
        return "assets/category-type/person.svg";
    }
  }
    
  private processData() {
    if (this.searchText) {
      this.isSearchResultOpen = true;
      console.log(`Going to search with term: "${this.searchText}"`);
      this.isLoading = true;
      // let filterByCategory = '';

      // switch (this.selectedCategory) {
      //   case QuickSearchCategory.person:
      //     filterByCategory = 'person_officer';
      //     break;
      //   case QuickSearchCategory.cases:
      //     filterByCategory = 'cases';
      //     break;
      // }

      this.quickNavService.performClientSideSearch(
        eSearchType.Navbar,
        this.searchText,
        1000,
        true
      ).subscribe((results) => {
        this.totalAmount = results.count;
        this.isLoading = false;
        this.quickSearchCategoryItems = results.items.map(item => this.mapQuickSearchItemData(item, results.mappingList ?? []));
      });;
    }

  }

  private mapQuickSearchItemData(item: IQuickSearchResponseItem, mappingList: IQuickNavResultTypeToUrl[], isRecentlyViewed?: boolean) {    
    //TODO: refactor this later
    const highlighted = isRecentlyViewed ? item : item['@search.highlights'];
    const regExp = /{{\s*(?<token>[^{}]+)\s*}}/gi;
    let categoryType: QuickSearchCategory = QuickSearchCategory.all;
    let resultIcon = 'person'
    switch (item.resulttype) {
      case CategoryAPIName.person:
        resultIcon = 'person'
        categoryType = QuickSearchCategory.person;
        break;
      case CategoryAPIName.officer:
        resultIcon = 'local_police'
        categoryType = QuickSearchCategory.officer;
        break;
      case CategoryAPIName.crimcase:
      case CategoryAPIName.crimparty:
        resultIcon = 'gavel'
        categoryType = QuickSearchCategory.crimcase;
        break;
      case CategoryAPIName.civilcase:
      case CategoryAPIName.civilparty:
        resultIcon = 'gavel'
        resultIcon = 'gavel'
        categoryType = QuickSearchCategory.civilcase;
        break;
      case CategoryAPIName.crimdoc:
        categoryType = QuickSearchCategory.crimdoc;
        break;
      case CategoryAPIName.civildoc:
        categoryType = QuickSearchCategory.civildoc;
        break;
      case CategoryAPIName.persondoc:
        categoryType = QuickSearchCategory.persondoc;
        break;
    }

    const arrObjKeys = Object.keys(item);
    const dictObj: any = {...item};
    let strUrl = '';
    const mapRecordForResultType = mappingList.find(ml => ml.ResultType == item.resulttype);
    if(  mapRecordForResultType ){
      strUrl = mapRecordForResultType.Url.replace(/{{\s*(?<token>[^{}]+)\s*}}/gi, function(rawVal, token: string){
        const strKey = arrObjKeys.find(k => k.toLowerCase() == token.toLowerCase());
        if( strKey ){
          const strVal = dictObj[strKey];
          return strVal;
        }
        return rawVal;
      });
      console.log(`Navigation url for resulttype: '${item.resulttype}' to '${strUrl}'`);
    }

    let responseItem: IQuickSearchItem = {      
      URL: strUrl,
      Name: item.name ?? '',
      CategoryType: categoryType,
      PersonName: highlighted?.lastfirstname ? highlighted.lastfirstname.toString() : item.lastfirstname,
      DOB: highlighted?.dob ? highlighted.dob.toString() : item.dob != null && item.dob.length > 0 ? item.dob.split(' ')[0] : '',
      SSN: highlighted?.ssnlistpartial ? this.checkFieldArray(highlighted.ssnlistpartial) : this.checkFieldArray(item.ssnlistpartial || []),
      PersonNumber: item.personnumber ?? item.personnumber_filterable,
      AliasPerson: highlighted?.aliaslastfirstname && highlighted?.aliaslastfirstname.length ? this.checkFieldArray(highlighted.aliaslastfirstname) : this.checkFieldArray(item.aliaslastfirstname || []),
      Agency: (highlighted?.agency ? highlighted.agency.toString() : (item.resulttype == 'officer' ? item.agency : undefined)),
      AgencyCode: highlighted?.agencycode ? highlighted.agencycode.toString() : undefined,
      DSN: (highlighted?.dsnpartial ? highlighted.dsnpartial.toString() : (item.resulttype == 'officer' ? item.dsnpartial : undefined)),
      Rank: (highlighted?.rank ? highlighted.rank.toString() : (item.resulttype == 'officer' ? item.rank : undefined)),
      DLNumber: highlighted?.dlnumberpartial && highlighted?.dlnumberpartial.length ? this.checkFieldArray(highlighted.dlnumberpartial) : undefined,
      TerminationDate: (item.resulttype == 'officer' ? item.terminationdate : undefined),
      ResultType: item.resulttype,
      Address: highlighted?.addresslistpartial && highlighted?.addresslistpartial.length ? this.checkFieldArray(highlighted.addresslistpartial) : undefined,
      ResultIcon: resultIcon,
      FileNo: highlighted?.filenumber ? highlighted.filenumber.toString() : item.filenumber,
      Court: highlighted?.courtnumber ? highlighted.courtnumber.toString() : item.courtnumber?.toString(),
      Matter: highlighted?.civilmatter ? highlighted.civilmatter.toString() : item.civilmatter,
      Report: highlighted?.reportnumber ? highlighted.reportnumber.toString() : item.reportnumber?.toString(),
      LeadCharge: highlighted?.leadcharge ? highlighted.leadcharge.toString() : item.leadcharge,
      // CivilMatter: highlighted?.civilmatter ? highlighted.civilmatter.toString() : item.civilmatter,
      // DocName: highlighted?.documentname ? highlighted.documentname.toString() : item.documentname,
      // JuvenileFlag: item.juvenileflag,
      // PersonInvolvement: item.personinvolvement,
      // TicketNumber: highlighted?.ticketnumber && highlighted?.ticketnumber.length ? this.checkFieldArray(highlighted.ticketnumber, isRecentlyViewed) : undefined,
      // LegacyFileNumber: highlighted?.legacyfilenumber ? highlighted.legacyfilenumber.toString() : undefined,
      // OCN: highlighted?.ocnpartial ? highlighted.ocnpartial.toString() : undefined,
      // PhoneNumber: highlighted?.phonenumber && highlighted?.phonenumber.length ? this.checkFieldArray(highlighted.phonenumber, isRecentlyViewed) : undefined,
      // ArrestNumber: highlighted?.arrestnumber ? highlighted.arrestnumber.toString() : undefined,
      // DocComment: highlighted?.documentcomment ? highlighted.documentcomment.toString() : undefined,
      // CivilDescription: highlighted?.civildescription ? highlighted.civildescription.toString() : undefined,
      // VictimWitnessName: highlighted?.victimwitnessname ? highlighted.victimwitnessname.toString() : undefined,
      // Content: highlighted?.content && highlighted?.content.length ? highlighted.content : undefined,
      // BadgeNumber: (highlighted?.badgenumber ? highlighted.badgenumber.toString() : (item.badgenumber ? item.badgenumber : undefined)),
      // BlobModifiedDate: highlighted?.blobmodifieddate ? highlighted.blobmodifieddate.toString() : item.blobmodifieddate,
      // BlobStorageId: highlighted?.blobstorageid ? highlighted.blobstorageid.toString() : item.blobstorageid,
      // Employer: highlighted?.employer ? highlighted.employer.toString() : item.employer,
      // CaseNotes: isRecentlyViewed && item.casenotes && item.casenotes.length ? item.casenotes.map(cn => cn.Notes).toString() : highlighted['casenotes/Notes'] && highlighted['casenotes/Notes'].length ? this.checkFieldArray(highlighted['casenotes/Notes'], isRecentlyViewed) : undefined,
      // PersonNotes: isRecentlyViewed && item.personnotes && item.personnotes.length ? item.personnotes.map(pn => pn.Notes).toString() : highlighted['personnotes/Notes'] && highlighted['personnotes/Notes'].length ? this.checkFieldArray(highlighted['personnotes/Notes'], isRecentlyViewed) : undefined,
      // EvidenceNotes: isRecentlyViewed && item.evidence && item.evidence.length ? item.evidence.map(e => e.Notes_searchable).toString() : highlighted['evidence/Notes_searchable'] && highlighted['evidence/Notes_searchable'].length ? this.checkFieldArray(highlighted['evidence/Notes_searchable'], isRecentlyViewed) : undefined,
      // EvidenceDescription: isRecentlyViewed && item.evidence && item.evidence.length ? item.evidence.map(e => e.Evidence_description).toString() : highlighted['evidence/Evidence_description'] && highlighted['evidence/Evidence_description'].length ? this.checkFieldArray(highlighted['evidence/Evidence_description'], isRecentlyViewed) : undefined,
      // SentenceNotes: isRecentlyViewed && item.chargelist && item.chargelist.length ? item.chargelist.map(ch => ch.SentenceNotes).toString() : highlighted['chargelist/SentenceNotes'] && highlighted['chargelist/SentenceNotes'].length ? this.checkFieldArray(highlighted['chargelist/SentenceNotes'], isRecentlyViewed) : undefined,
      // LabNumber: isRecentlyViewed && item.labnumbers && item.labnumbers.length ? item.labnumbers.map(ln => ln?.LabNumberPartial).toString() : highlighted['labnumbers/LabNumberPartial'] && highlighted['labnumbers/LabNumberPartial'].length ? this.checkFieldArray(highlighted['labnumbers/LabNumberPartial'], isRecentlyViewed) : undefined,
      // LabNotes: isRecentlyViewed && item.labnumbers && item.labnumbers.length ? item.labnumbers.map(ln => ln?.LabNotes).toString() : highlighted['labnumbers/LabNotes'] && highlighted['labnumbers/LabNotes'].length ? this.checkFieldArray(highlighted['labnumbers/LabNotes'], isRecentlyViewed) : undefined,
      // InvestigatorNotes: isRecentlyViewed && item.investigatornotes && item.investigatornotes.length ? item.investigatornotes.map(inv => inv.Notes).toString() : highlighted['investigatornotes/Notes'] && highlighted['investigatornotes/Notes'].length ? this.checkFieldArray(highlighted['investigatornotes/Notes'], isRecentlyViewed) : undefined,
      // InvestigatorServicesNotes: isRecentlyViewed && item.investigatorservices && item.investigatorservices.length ? item.investigatorservices.map(ivs => ivs.ServiceNotes).toString() : highlighted['investigatorservices/ServiceNotes'] && highlighted['investigatorservices/ServiceNotes'].length ? this.checkFieldArray(highlighted['investigatorservices/ServiceNotes'], isRecentlyViewed) : undefined,
      // TimeEntryDescription: isRecentlyViewed && item.timeentries && item.timeentries.length ? item.timeentries.map(te => te.TimeDescription).toString() : highlighted['timeentries/TimeDescription'] && highlighted['timeentries/TimeDescription'].length ? this.checkFieldArray(highlighted['timeentries/TimeDescription'], isRecentlyViewed) : undefined,
      // AdditionalAddress: isRecentlyViewed && item.additionaladdresses && item.additionaladdresses.length ? item.additionaladdresses.map(a => a.FullAddress).toString() : highlighted['additionaladdresses/FullAddressPartial'] && highlighted['additionaladdresses/FullAddressPartial'].length ? this.checkFieldArray(highlighted['additionaladdresses/FullAddressPartial'], isRecentlyViewed) : undefined,
      // AdditionalAddressDescription: isRecentlyViewed && item.additionaladdresses && item.additionaladdresses.length ? item.additionaladdresses.map(a => a.Description).toString() : highlighted['additionaladdresses/Description'] && highlighted['additionaladdresses/Description'].length ? this.checkFieldArray(highlighted['additionaladdresses/Description'], isRecentlyViewed) : undefined,

    }

    const anyItem: any = responseItem;
    const regexNewline = new RegExp('<br[^>]+>', 'gi');
    for (const [key, value] of Object.entries(responseItem)) {
      if (typeof value === 'string') {
        const strVal: string = value?.toString();
        if (strVal) {
          const strNewVal = strVal.replace(regexNewline, " ").replace("\n", " ").replace("\r", "");
          anyItem[key] = strNewVal;
        }
      }
    }
    responseItem = { ...anyItem };
    return responseItem;

  }

  private checkFieldArray(fieldValue: string[]): string {
    return fieldValue.length > 0 ? fieldValue[0] : '';
  }


}
